import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Row,
  Col,
  Statistic,
  Select,
  Modal,
  Form,
  Input,
  DatePicker,
  message,
  Checkbox,
  Space,
  Spin,
  Tabs,
  Tooltip,
  Avatar,
  Progress,
  Timeline,
  Badge,
  Dropdown,
  Menu,
  Typography,
  Divider,
  Popconfirm,
  Alert,
  Empty,
  Radio
} from 'antd';
import {
  TeamOutlined,
  ClockCircleOutlined,
  UserSwitchOutlined,
  ReloadOutlined,
  ExportOutlined,
  FilterOutlined,
  EyeOutlined,
  LineChartOutlined,
  DashboardOutlined,
  DownloadOutlined,
  HistoryOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CarOutlined,
  SearchOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  TableOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  NumberOutlined,
  FieldTimeOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EnvironmentOutlined, CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import API from '../../API/API';
import { formatUSPhone } from "../../Utils"
import LeadLocalTime from '../LeadLocalTime/LeadLocalTime.jsx';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);


dayjs.extend(relativeTime);
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

const QueueDashboard = () => {
  const user = JSON.parse(localStorage.getItem('userData')) || {};
  const isAgent = user.role === 'SALES-AGENT';
  const isAdmin = ['ADMIN', 'SUPERVISOR'].includes(user.role);

  // State Management
  const [loading, setLoading] = useState(false);
  // Separate flag so the lead table (which carries the existing-customer badge
  // and previous-agent line) renders as soon as the lead list resolves, instead
  // of waiting on the counts/stats/dashboard calls.
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [queuedLeads, setQueuedLeads] = useState([]);
  const [queueCounts, setQueueCounts] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [queueDashboard, setQueueDashboard] = useState(null);
  const [agents, setAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetailsModal, setLeadDetailsModal] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [bulkTransferModal, setBulkTransferModal] = useState(false);
  const [reassignModal, setReassignModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [activeTab, setActiveTab] = useState('queue');
  const [showInactiveAgents, setShowInactiveAgents] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferForm] = Form.useForm();
  const [bulkTransferForm] = Form.useForm();
const [datePreset, setDatePreset] = useState('thisYear');


  // Filters
  // The default date range is seeded here rather than in a mount effect, so the
  // first fetch already carries it. Same range, same query, one round instead
  // of two. Matches the 'thisYear' datePreset default above.
  const [filters, setFilters] = useState(() => {
    const now = dayjs().tz('America/Los_Angeles');

    return {
      tier: '',
      status: 'queued',
      agentId: '',
      domain: '',
      search: '',
      startDate: now.startOf('year'),
      endDate: now.endOf('year'),
      page: 1,
      limit: 30,
      sortBy: 'queuedAt',
      sortOrder: 'desc'
    };
  });

  // Debug: Log lead structure
  useEffect(() => {
    if (queuedLeads.length > 0) {
      console.log('🔍 First lead structure:', {
        id: queuedLeads[0]._id,
        leadId: queuedLeads[0].leadId,
        allFields: Object.keys(queuedLeads[0])
      });
    }
  }, [queuedLeads]);

  // Fetch all agents
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const users = await API.userGetAll();
        if (users?.success) {
          const allAgentsList = users.data
            .filter(u => u.role === 'SALES-AGENT' && !u.isDeleted)
            .map(agent => ({
              ...agent,
              fullName: `${agent.firstName} ${agent.lastName}`,
              displayName: `${agent.firstName} ${agent.lastName} (${agent.subRole}) ${agent.isActive ? '✅' : '⛔'}`,
              isActiveDisplay: agent.isActive ? 'Active' : 'Inactive'
            }));

          setAgents(allAgentsList.filter(agent => agent.isActive));
          setAllAgents(allAgentsList);
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
    };

    fetchAgents();
  }, []);


const handleDatePresetChange = (preset) => {
  setDatePreset(preset);
  const now = dayjs().tz('America/Los_Angeles');
  let startDate, endDate;

  switch (preset) {
    case 'today':
      startDate = now.startOf('day');
      endDate = now.endOf('day');
      break;
    case 'yesterday':
      startDate = now.subtract(1, 'day').startOf('day');
      endDate = now.subtract(1, 'day').endOf('day');
      break;
    case 'thisWeek':
      startDate = now.startOf('week');
      endDate = now.endOf('week');
      break;
    case 'thisMonth':
      // This Month: June 1st 00:00:00 to June 30th 23:59:59 PST
      startDate = now.startOf('month');
      endDate = now.endOf('month');
      break;
    case 'lastMonth':
      // Last Month: May 1st 00:00:00 to May 31st 23:59:59 PST
      startDate = now.subtract(1, 'month').startOf('month');
      endDate = now.subtract(1, 'month').endOf('month');
      break;
    case 'thisYear':
      startDate = now.startOf('year');
      endDate = now.endOf('year');
      break;
    case 'custom':
      return;
    default:
      startDate = now.startOf('month');
      endDate = now.endOf('month');
  }

  console.log('📅 Date Preset Changed:', {
    preset,
    startDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
    endDate: endDate.format('YYYY-MM-DD HH:mm:ss')
  });

  setFilters({
    ...filters,
    startDate,
    endDate
  });
};
const fetchAllData = useCallback(async () => {
  setLoading(true);
  setLeadsLoading(true);
  try {
    // Convert PST/PDT dates to UTC for API with proper month boundaries
    let startDateUTC = null;
    let endDateUTC = null;

    if (!isAgent && filters.startDate && filters.endDate) {
      // Get the dates in Pacific Time
      const startPST = dayjs(filters.startDate).tz('America/Los_Angeles');
      const endPST = dayjs(filters.endDate).tz('America/Los_Angeles');

      // Convert to UTC for database query
      // Start date: beginning of the day in PST
      // End date: end of the day in PST
      startDateUTC = startPST.startOf('day').utc().format();
      endDateUTC = endPST.endOf('day').utc().format();

      console.log('📅 Date Conversion Debug:', {
        filterType: datePreset,
        startPST: startPST.format('YYYY-MM-DD HH:mm:ss'),
        endPST: endPST.format('YYYY-MM-DD HH:mm:ss'),
        startUTC: startDateUTC,
        endUTC: endDateUTC,
        timezone: dayjs.tz.guess()
      });
    }

    // Same four calls as before, still fired together. The difference is that
    // each one now updates its own panel the moment it resolves, so the lead
    // table no longer waits on the slowest of the four.
    const settle = (promise) =>
      promise.then(
        (value) => ({ status: 'fulfilled', value }),
        (reason) => ({ status: 'rejected', reason })
      );

    const leadsPromise = isAgent
      ? API.fetchMyQueuedLeads({ status: 'all' })
      : API.fetchQueuedLeads({
          ...filters,
          startDate: startDateUTC,
          endDate: endDateUTC
        });

    const countsPromise = API.fetchQueuedCounts();
    const statsPromise = isAdmin ? API.fetchQueuedStats({ days: 7 }) : null;
    const dashboardPromise = isAdmin ? API.fetchQueueDashboard() : null;

    // 1. Queue Leads - renders as soon as this one lands
    const leadsTask = settle(leadsPromise).then((leadsResult) => {
      let fetchedLeads = [];

      if (!isAgent && leadsResult.status === 'fulfilled' && leadsResult.value?.success) {
        fetchedLeads = leadsResult.value.data || [];
      }

      if (isAgent && leadsResult.status === 'fulfilled' && leadsResult.value?.success) {
        // For agent view, combine queued and assigned leads
        const agentData = leadsResult.value.data;
        fetchedLeads = [
          ...(agentData?.leads?.queued || []),
          ...(agentData?.leads?.assigned || [])
        ];
      }

      // Apply client-side filtering to ensure correct timezone-based month filtering
      let filteredLeads = fetchedLeads;

      if (!isAgent && filters.startDate && filters.endDate) {
        filteredLeads = fetchedLeads.filter(lead => {
          // Get the lead's queued date in Pacific Time
          const leadQueuedUTC = lead.assignment?.queuedAt || lead.queuedAt;
          if (!leadQueuedUTC) return false;

          const leadPST = dayjs(leadQueuedUTC).tz('America/Los_Angeles');
          const startPST = dayjs(filters.startDate).tz('America/Los_Angeles').startOf('day');
          const endPST = dayjs(filters.endDate).tz('America/Los_Angeles').endOf('day');

          // Check if the lead falls within the date range
          const isInRange = leadPST.isBetween(startPST, endPST, null, '[]');

          // Debug log for Daniel Bishop to see why it's appearing in both months
          if (lead.customer?.name === 'Daniel Bishop') {
            console.log('🔍 Daniel Bishop date check:', {
              leadUTC: leadQueuedUTC,
              leadPST: leadPST.format('YYYY-MM-DD HH:mm:ss'),
              startPST: startPST.format('YYYY-MM-DD HH:mm:ss'),
              endPST: endPST.format('YYYY-MM-DD HH:mm:ss'),
              filterType: datePreset,
              isInRange
            });
          }

          return isInRange;
        });

        console.log(`📊 Filtered leads: ${fetchedLeads.length} -> ${filteredLeads.length} (${datePreset} filter)`);
      }

      setQueuedLeads(filteredLeads);
      setLeadsLoading(false);
    });

    // 2. Queue Counts
    const countsTask = settle(countsPromise).then((countsResult) => {
      if (countsResult.status === 'fulfilled' && countsResult.value?.success) {
        setQueueCounts(countsResult.value.data);
      }
    });

    // 3. Queue Stats (only for admin)
    const statsTask = isAdmin
      ? settle(statsPromise).then((statsResult) => {
          if (statsResult.status === 'fulfilled' && statsResult.value?.success) {
            setQueueStats(statsResult.value.data);
          }
        })
      : Promise.resolve();

    // 4. Queue Dashboard (only for admin)
    const dashboardTask = isAdmin
      ? settle(dashboardPromise).then((dashboardResult) => {
          if (dashboardResult.status === 'fulfilled' && dashboardResult.value?.success) {
            setQueueDashboard(dashboardResult.value.data);
          }
        })
      : Promise.resolve();

    // Wait for all panels before clearing the global busy state
    await Promise.all([leadsTask, countsTask, statsTask, dashboardTask]);

    message.success('Data refreshed successfully');
  } catch (error) {
    console.error('Error fetching data:', error);
    message.error('Failed to load data');
  } finally {
    setLoading(false);
    setLeadsLoading(false);
  }
}, [filters, isAgent, isAdmin, datePreset]);
  // Initial load
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Handle remove from queue
  const handleRemoveFromQueue = async (leadId) => {
    try {
      console.log('🗑️ Attempting to remove lead from queue:', leadId);

      // Check if it's a valid ID
      if (!leadId || leadId.length < 10) {
        message.error('Invalid lead ID format');
        return;
      }

      const response = await API.removeFromQueue(leadId);

      console.log('📨 Remove response:', response);

      if (response.success) {
        message.success('Lead removed from queue successfully');

        // Update local state - check both _id and leadId fields
        setQueuedLeads(prev => prev.filter(lead => {
          const leadIdToCheck = lead._id || lead.leadId;
          return leadIdToCheck !== leadId;
        }));

        // Update counts
        if (queueCounts) {
          setQueueCounts(prev => ({
            ...prev,
            totalQueuedLeads: Math.max(0, (prev.totalQueuedLeads || 1) - 1)
          }));
        }

        // Refresh data
        fetchAllData();
      } else {
        message.error(response.error || 'Failed to remove from queue');
      }
    } catch (error) {
      console.error('❌ Remove from queue error:', error);
      message.error('Failed to remove from queue');
    }
  };

  // Handle bulk remove from queue
  const handleBulkRemoveFromQueue = async () => {
    if (selectedIds.length === 0) {
      message.warning('Please select leads to remove');
      return;
    }

    Modal.confirm({
      title: 'Remove Selected Leads from Queue',
      content: (
        <div>
          <p>Are you sure you want to remove {selectedIds.length} lead(s) from the queue?</p>
          <Alert
            message="Important"
            description="Leads will be removed from the queue but will remain in the system. They can be requeued later."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      ),
      okText: 'Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        try {
          console.log('🗑️ Bulk removing leads:', selectedIds);

          const response = await API.bulkRemoveFromQueue({ leadIds: selectedIds });

          console.log('📨 Bulk remove response:', response);

          if (response.success) {
            const removedCount = response.data?.removedCount || selectedIds.length;
            message.success(`${removedCount} lead(s) removed from queue`);

            // Update local state
            setQueuedLeads(prev => prev.filter(lead => {
              const leadId = lead._id || lead.leadId;
              return !selectedIds.includes(leadId);
            }));

            // Update counts
            if (queueCounts) {
              setQueueCounts(prev => ({
                ...prev,
                totalQueuedLeads: Math.max(0, (prev.totalQueuedLeads || 1) - removedCount)
              }));
            }

            setSelectedIds([]);
            fetchAllData();
          } else {
            message.error(response.error || 'Bulk remove failed');
          }
        } catch (error) {
          console.error('❌ Bulk remove error:', error);
          message.error('Bulk remove failed');
        }
      }
    });
  };

  // Handle single lead transfer
  const handleTransfer = async (values) => {
    setTransferLoading(true);
    try {
      const response = await API.transferQueuedLead(selectedLead.leadId, {
        targetAgentId: values.targetAgentId,
        reason: values.reason
      });

      if (response.success) {
        message.success('Lead transferred and assigned successfully');
        transferForm.resetFields();
        setTransferModal(false);
        setSelectedLead(null);
        fetchAllData();
      } else {
        message.error(response.error || 'Transfer failed');
      }
    } catch (error) {
      console.error('Transfer error:', error);
      message.error('Transfer failed. Please try again.');
    } finally {
      setTransferLoading(false);
    }
  };

  // Handle bulk transfer
  const handleBulkTransfer = async (values) => {
    if (selectedIds.length === 0) {
      message.warning('Please select leads to transfer');
      return;
    }

    try {
      const response = await API.bulkTransferQueuedLeads({
        leadIds: selectedIds,
        targetAgentId: values.targetAgentId,
        reason: values.reason
      });

      if (response.success) {
        message.success(`${selectedIds.length} leads transferred successfully`);
        bulkTransferForm.resetFields();
        setBulkTransferModal(false);
        setSelectedIds([]);
        fetchAllData();
      } else {
        message.error(response.error || 'Bulk transfer failed');
      }
    } catch (error) {
      console.error('Bulk transfer error:', error);
      message.error('Bulk transfer failed');
    }
  };

  // Handle reassign
  const handleReassign = async (leadId, reason = 'Automatic reassignment') => {
    try {
      const response = await API.reassignQueuedLead(leadId, { reason });
      if (response.success) {
        message.success('Lead reassigned to next available agent');
        fetchAllData();
      } else {
        message.error(response.error || 'Reassign failed');
      }
    } catch (error) {
      console.error('Reassign error:', error);
      message.error('Reassign failed');
    }
  };

  // Handle export
  const handleExport = async (values) => {
    try {
      const params = {
        format: values.format,
        tier: values.tier,
       startDate: values.dateRange?.[0]?.tz('America/Los_Angeles').startOf('day').utc().format(),
endDate: values.dateRange?.[1]?.tz('America/Los_Angeles').endOf('day').utc().format()
      };

      const response = await API.exportQueuedLeads(params);

      if (values.format === 'csv') {
        const blob = new Blob([response], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `queued_leads_${dayjs().tz('America/Los_Angeles').format('YYYY-MM-DD')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else if (values.format === 'excel') {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
       a.download = `queued_leads_${dayjs().tz('America/Los_Angeles').format('YYYY-MM-DD')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      message.success('Export completed');
      setExportModal(false);
    } catch (error) {
      console.error('Export error:', error);
      message.error('Export failed');
    }
  };

  // Load lead details
  const loadLeadDetails = async (leadId) => {
    try {
      console.log('Loading lead details for ID:', leadId);
      const response = await API.fetchQueuedLeadById(leadId);
      if (response.success) {
        setSelectedLead(response.data);
        setLeadDetailsModal(true);
      } else {
        message.error(response.error || 'Failed to load lead details');
      }
    } catch (error) {
      console.error('Error loading lead details:', error);
      message.error('Failed to load lead details');
    }
  };
  console.log(selectedLead, "--------------??????????")

  // Get agents by tier
  const getAgentsByTier = (tier) => {
    return agents.filter(agent => agent.subRole === tier);
  };


  // Table columns
  const columns = [
    !isAgent && {
      title: (
        <Checkbox
          indeterminate={selectedIds.length > 0 && selectedIds.length < queuedLeads.length}
          checked={selectedIds.length === queuedLeads.length && queuedLeads.length > 0}
          onChange={(e) => {
            if (e.target.checked) {
              // Use _id or leadId
              setSelectedIds(queuedLeads.map(lead => lead._id || lead.leadId));
            } else {
              setSelectedIds([]);
            }
          }}
        />
      ),
      width: 50,
      render: (_, record) => {
        const recordId = record._id || record.leadId;
        return (
          <Checkbox
            checked={selectedIds.includes(recordId)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedIds([...selectedIds, recordId]);
              } else {
                setSelectedIds(selectedIds.filter(id => id !== recordId));
              }
            }}
          />
        );
      }
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 220,
      render: (_, record) => {
        const phoneResult = formatUSPhone(record?.customer?.phone || '');
        const previousAgent = record?.assignment?.previousAgent || record?.assignment?.preferredAgent;

        return (
          <div>
            {/* Name + customer local time */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500, fontSize: '14px' }}>
                {record?.customer?.name || '—'}
              </span>
              <LeadLocalTime
                zipCode={record?.customer?.zipCode}
                browserTimeZone={record?.customer?.browserTimeZone}
              />
            </div>

            {/* Phone - just the number, clean look */}
            <div style={{ fontSize: '13px', color: '#333' }}>
              <PhoneOutlined style={{ marginRight: 6, color: '#555' }} />
              {phoneResult.formatted}
            </div>

            {/* Email */}
            <div style={{ fontSize: '12px', color: '#555' }}>
              <MailOutlined style={{ marginRight: 6, color: '#555' }} />
              {record?.customer?.email || '—'}
            </div>

            <div style={{ marginTop: 6 }}>
              {record?.assignment?.isExistingCustomer && (
                <Tag color="green" style={{ marginBottom: 4 }}>
                  Existing Customer
                </Tag>
              )}
              {previousAgent && (
                <div style={{ fontSize: '11px', color: '#666' }}>
                  Previous Agent: {previousAgent.name || previousAgent.email || 'N/A'}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Vehicle',
      key: 'vehicle',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <CarOutlined /> {record.vehicle.part}
          </div>
          <div style={{ fontSize: '11px', color: '#888' }}>
            {record.vehicle.year} {record.vehicle.make} {record.vehicle.model}
          </div>
          {record.vehicle.vin && (
            <div style={{ fontSize: '10px', color: '#aaa' }}>
              VIN: {record.vehicle.vin.substring(0, 8)}...
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Tier',
      key: 'tier',
      width: 100,
      render: (_, record) => {
        const tierColors = {
          'TIER-1': 'blue',
          'TIER-2': 'green',
          'TIER-3': 'orange'
        };
        return (
          <Tag color={tierColors[record.assignment.tier]} style={{ fontWeight: 'bold' }}>
            {record.assignment.tier}
          </Tag>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={record.status === 'assigned' ? 'green' : 'orange'}>
          {record.status === 'assigned' ? 'ASSIGNED' : 'QUEUED'}
        </Tag>
      )
    },
    // In your table columns, ensure dates display in Pacific Time
{
  title: 'Queue',
  key: 'queue',
  width: 150,
  render: (_, record) => (
    <div>
      {record.status === 'queued' ? (
        <>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>
            <NumberOutlined /> Position: #{record.queuePosition || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <FieldTimeOutlined /> Wait: {record.estimatedWaitTime || 0} mins
          </div>
        </>
      ) : (
        <div style={{ fontSize: '14px', fontWeight: 500, color: '#52c41a' }}>
          <CheckCircleOutlined /> Assigned
        </div>
      )}
      <div style={{ fontSize: '14px', color: '#888', fontWeight: "bold" }}>
        {dayjs(record.assignment.queuedAt)
          .tz('America/Los_Angeles')
          .format('MMM DD, YYYY • hh:mm:ss A')}
      </div>
    </div>
  )
},
    !isAgent && {
      title: 'Sales-Agent',
      key: 'agent',
      width: 180,
      render: (_, record) => (
        record.agent ? (
          <div>
            <Avatar
              size="small"
              icon={<UserOutlined />}
              style={{
                marginRight: 8,
                backgroundColor: record.agent.isActive ? '#52c41a' : '#ff4d4f'
              }}
            />
            <span>{record.agent.name}</span>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {record.agent.tier} • {record.agent.isActive ? 'Active' : 'Inactive'}
            </div>
          </div>
        ) : (
          <Tag color="default">Not Assigned</Tag>
        )
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => {
        const leadId = record._id || record.leadId;
        return (
          <Space size="small">
            <Tooltip title="View Details">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click
                  loadLeadDetails(leadId);
                }}
              />
            </Tooltip>

            {isAdmin && (
              <>
                <Tooltip title="Transfer">
                  <Button
                    type="text"
                    icon={<UserSwitchOutlined />}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click
                      setSelectedLead(record);
                      setTransferModal(true);
                    }}
                  />
                </Tooltip>

                <Tooltip title="Reassign to next agent">
                  <Popconfirm
                    title="Reassign this lead to next available agent?"
                    onConfirm={(e) => {
                      if (e && e.stopPropagation) e.stopPropagation();
                      handleReassign(leadId);
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      icon={<ReloadOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                </Tooltip>

                {/* DELETE BUTTON */}
                <Tooltip title="Remove from Queue">
                  <Popconfirm
                    title={
                      <div>
                        <p>Are you sure you want to remove this lead from the queue?</p>
                        <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                          This will unqueue the lead but NOT delete it from the system.
                        </p>
                      </div>
                    }
                    onConfirm={(e) => {
                      if (e && e.stopPropagation) e.stopPropagation();
                      handleRemoveFromQueue(leadId);
                    }}
                    okText="Remove"
                    cancelText="Cancel"
                    okType="danger"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                </Tooltip>
              </>
            )}
          </Space>
        );
      }
    }
  ].filter(Boolean);

// Filter component
const FilterPanel = () => (
  <Card
    title={
      <Space>
        <FilterOutlined />
        <span>Filters</span>
      </Space>
    }
    size="small"
    style={{ marginBottom: 16 }}
    extra={
      !isAgent && (
        <Space>
          <Radio.Group
            value={showInactiveAgents}
            onChange={(e) => {
              setShowInactiveAgents(e.target.value);
              if (e.target.value) {
                setAgents(allAgents);
              } else {
                setAgents(allAgents.filter(agent => agent.isActive));
              }
            }}
            size="small"
          >
            <Radio.Button value={false}>Active Agents Only</Radio.Button>
            <Radio.Button value={true}>Show All Agents</Radio.Button>
          </Radio.Group>
        </Space>
      )
    }
  >
    <Row gutter={[16, 16]} align="middle">
      <Col span={6}>
        <Select
          placeholder="Tier"
          style={{ width: '100%' }}
          value={filters.tier}
          onChange={(value) => setFilters({ ...filters, tier: value })}
          allowClear
          disabled={isAgent}
        >
          <Option value="TIER-1">Tier 1</Option>
          <Option value="TIER-2">Tier 2</Option>
          <Option value="TIER-3">Tier 3</Option>
        </Select>
      </Col>

      {!isAgent && (
        <Col span={6}>
          <Select
            placeholder="Status"
            style={{ width: '100%' }}
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
          >
            <Option value="queued">Queued</Option>
            <Option value="assigned">Assigned</Option>
            <Option value="all">All</Option>
          </Select>
        </Col>
      )}

      {!isAgent && (
        <Col span={6}>
          <Select
            placeholder="Agent"
            style={{ width: '100%' }}
            value={filters.agentId}
            onChange={(value) => setFilters({ ...filters, agentId: value })}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {agents.map(agent => (
              <Option key={agent._id} value={agent._id}>
                {agent.displayName}
              </Option>
            ))}
          </Select>
        </Col>
      )}

      {/* MOVED: Date Preset Selector + RangePicker */}
      <Col span={isAgent ? 12 : 6}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            value={datePreset}
            onChange={handleDatePresetChange}
            style={{ width: '100%' }}
            size="middle"
          >
            <Option value="today">Today</Option>
            <Option value="yesterday">Yesterday</Option>
            <Option value="thisWeek">This Week</Option>
            <Option value="thisMonth">This Month</Option>
            <Option value="lastMonth">Last Month</Option>
            <Option value="thisYear">This Year</Option>
            <Option value="custom">Custom Range</Option>
          </Select>
          
          <RangePicker
            value={[filters.startDate, filters.endDate]}
            onChange={(dates) => {
              setDatePreset('custom');
              setFilters({
                ...filters,
                startDate: dates?.[0],
                endDate: dates?.[1]
              });
            }}
            style={{ width: '100%' }}
          />
        </Space>
      </Col>

      <Col span={18}>
        <Input
          placeholder="Search by name, phone, email, VIN..."
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          allowClear
        />
      </Col>

      <Col span={6}>
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={fetchAllData}
            loading={loading}
          >
            Apply
          </Button>
       <Button
  onClick={() => {
    const now = dayjs().tz('America/Los_Angeles');
    setFilters({
      tier: '',
      status: 'queued',
      agentId: '',
      domain: '',
      search: '',
      startDate: now.startOf('month'),
      endDate: now.endOf('month'), // Fixed: was endOf('month')
      page: 1,
      limit: 20,
      sortBy: 'queuedAt',
      sortOrder: 'desc'
    });
    setDatePreset('thisMonth');
    setSelectedIds([]);
  }}
>
  Reset
</Button>
        </Space>
      </Col>
    </Row>
  </Card>
);

  // Stats panel
  const StatsPanel = () => {
    if (!queueCounts) return null;

    return (
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card hoverable>
            <Statistic
              title="Total Queued"
              value={queueCounts.totalQueuedLeads || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card hoverable>
            <Statistic
              title="Avg Wait Time"
              value={Math.round(
                queueCounts.byTier?.reduce((sum, tier) => sum + (tier.avgWaitTime || 0), 0) /
                (queueCounts.byTier?.length || 1)
              )}
              suffix="mins"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card hoverable>
            <Statistic
              title="Available Agents"
              value={agents.filter(a => a.isActive).length}
              prefix={<UserOutlined />}
              valueStyle={{ color: agents.filter(a => a.isActive).length > 0 ? '#722ed1' : '#ff4d4f' }}
            />
          </Card>
        </Col>

        <Col span={6}>
          <Card hoverable>
            <Statistic
              title="High Priority"
              value={queueCounts.summary?.needsAttention?.length || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
              suffix="Tiers"
            />
          </Card>
        </Col>
      </Row>
    );
  };

  // Tier analysis panel
  const TierAnalysisPanel = () => {
    if (!queueCounts?.byTier) return null;

    return (
      <Card
        title="Tier Analysis"
        style={{ marginTop: 16 }}
        extra={
          <Button type="text" icon={<BarChartOutlined />} onClick={() => setActiveTab('analytics')} />
        }
      >
        {queueCounts.byTier.map(tier => {
          const tierAgents = getAgentsByTier(tier.tier);
          const activeAgentsCount = tierAgents.filter(a => a.isActive).length;

          return (
            <div key={tier.tier} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <Tag color={
                    tier.tier === 'TIER-1' ? 'blue' :
                      tier.tier === 'TIER-2' ? 'green' : 'orange'
                  }>
                    {tier.tier}
                  </Tag>
                  <span style={{ marginLeft: 8, fontWeight: 500 }}>{tier.count} leads</span>
                </div>
                <div>
                  <Tag color={
                    activeAgentsCount === 0 ? 'red' :
                      tier.count > activeAgentsCount * 10 ? 'red' :
                        tier.count > activeAgentsCount * 5 ? 'orange' : 'green'
                  }>
                    {activeAgentsCount === 0 ? 'NO AGENTS' :
                      tier.count > activeAgentsCount * 10 ? 'HIGH LOAD' :
                        tier.count > activeAgentsCount * 5 ? 'MEDIUM LOAD' : 'LOW LOAD'}
                  </Tag>
                </div>
              </div>

              <Progress
                percent={Math.min((tier.count / (activeAgentsCount * 15 || 1)) * 100, 100)}
                status={
                  activeAgentsCount === 0 ? 'exception' :
                    tier.count > activeAgentsCount * 10 ? 'exception' :
                      tier.count > activeAgentsCount * 5 ? 'active' : 'success'
                }
                format={() => `${activeAgentsCount} agents • ${tier.leadsPerAgent || 'N/A'} leads/agent`}
              />

              <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                {tier.availableAgents || 0} total agents • {tier.avgWaitTime || 0} min avg wait • {tier.estimatedProcessingTime || 0} min total
              </div>
            </div>
          );
        })}
      </Card>
    );
  };

  // Agent status component
  const AgentStatusPanel = () => {
    if (agents.length === 0) {
      return (
        <Alert
          message="No Agents Available"
          description="There are currently no active agents in the system. Please activate some agents to handle queued leads."
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      );
    }

    return (
      <Card title="Agent Status" style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          {agents.slice(0, 6).map(agent => (
            <Col span={8} key={agent._id}>
              <Card
                size="small"
                title={
                  <Space>
                    <Avatar
                      size="small"
                      icon={<UserOutlined />}
                      style={{
                        backgroundColor: agent.isActive ? '#52c41a' : '#ff4d4f'
                      }}
                    />
                    <span>{agent.firstName} {agent.lastName}</span>
                  </Space>
                }
                extra={
                  <Tag color={agent.isActive ? 'success' : 'error'}>
                    {agent.isActive ? 'Active' : 'Inactive'}
                  </Tag>
                }
              >
                <div style={{ fontSize: '12px' }}>
                  <div><strong>Tier:</strong> {agent.subRole}</div>
                  <div><strong>Email:</strong> {agent.email}</div>
                  <div><strong>Status:</strong> {agent.status || 'Available'}</div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  // Add extra actions to the Tabs
  const tabBarExtraContent = useMemo(() => {
    return (
      <Space>
        {!isAgent && selectedIds.length > 0 && (
          <Space>
            <Badge count={selectedIds.length}>
              <Button
                type="primary"
                icon={<UserSwitchOutlined />}
                onClick={() => setBulkTransferModal(true)}
              >
                Bulk Transfer
              </Button>
            </Badge>

            {/* BULK DELETE BUTTON */}
            <Popconfirm
              title={`Remove ${selectedIds.length} selected lead(s) from queue?`}
              onConfirm={handleBulkRemoveFromQueue}
              okText="Remove"
              cancelText="Cancel"
              okType="danger"
            >
              <Button
                danger
                icon={<CloseCircleOutlined />}
              >
                Remove Selected
              </Button>
            </Popconfirm>
          </Space>
        )}

        {isAdmin && (
          <Button
            icon={<ExportOutlined />}
            onClick={() => setExportModal(true)}
          >
            Export
          </Button>
        )}

        <Button
          icon={<ReloadOutlined />}
          onClick={fetchAllData}
          loading={loading}
        >
          Refresh
        </Button>
      </Space>
    );
  }, [isAgent, isAdmin, selectedIds.length, loading, fetchAllData]);

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <DashboardOutlined /> Queue Management
        </Title>
        <Text type="secondary">
          Manage and monitor queued leads in real-time
          {isAgent && ` - Welcome ${user.firstName} ${user.lastName}`}
        </Text>
      </div>

      {/* Stats Panel */}
      <StatsPanel />

      {/* Main Content */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        style={{ marginTop: 16 }}
        tabBarExtraContent={tabBarExtraContent}
      >
        <TabPane tab={<span><TableOutlined /> Queue</span>} key="queue">
          {/* Filter Panel */}
          <FilterPanel />

          {/* Alerts */}
          {!isAgent && queueDashboard?.alerts?.map((alert, index) => (
            <Alert
              key={index}
              message={`${alert.tier}: ${alert.message}`}
              type={alert.severity === 'CRITICAL' ? 'error' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
            />
          ))}

         
          {/* Agent Status - Only for admin */}
          {!isAgent && <AgentStatusPanel />}
 {/* Tier Analysis - Only for admin */}
          {!isAgent && <TierAnalysisPanel />}

          {/* Queue Table */}
          <Card
            title={`${isAgent ? 'My ' : ''}Queued Leads (${queuedLeads.length})`}
            extra={
              !isAgent && (
                <Space>
                  {!isAgent && selectedIds.length > 0 && (
                    <Space>
                      <Badge count={selectedIds.length}>
                        <Button
                          type="primary"
                          icon={<UserSwitchOutlined />}
                          onClick={() => setBulkTransferModal(true)}
                        >
                          Bulk Transfer
                        </Button>
                      </Badge>

                      {/* BULK DELETE BUTTON */}
                      <Popconfirm
                        title={`Remove ${selectedIds.length} selected lead(s) from queue?`}
                        onConfirm={handleBulkRemoveFromQueue}
                        okText="Remove"
                        cancelText="Cancel"
                        okType="danger"
                      >
                        <Button
                          danger
                          icon={<CloseCircleOutlined />}
                        >
                          Remove Selected
                        </Button>
                      </Popconfirm>
                    </Space>
                  )}
                  <Select
                    value={filters.sortOrder}
                    onChange={(value) => setFilters({ ...filters, sortOrder: value })}
                    style={{ width: 120 }}
                  >
                    <Option value="desc">
                      <SortDescendingOutlined /> Newest First
                    </Option>
                    <Option value="asc">
                      <SortAscendingOutlined /> Oldest First
                    </Option>
                  </Select>
                  <Select
                    value={filters.limit}
                    onChange={(value) => setFilters({ ...filters, limit: value, page: 1 })}
                    style={{ width: 100 }}
                  >
                    <Option value={10}>10 / page</Option>
                    <Option value={20}>20 / page</Option>
                    <Option value={30}>30 / page</Option>
                    <Option value={50}>50 / page</Option>
                    <Option value={100}>100 / page</Option>
                  </Select>
                </Space>
              )
            }
            style={{ marginTop: 16 }}
          >
            {leadsLoading ? (
              <div style={{ textAlign: 'center', padding: 50 }}>
                <Spin size="large" />
              </div>
            ) : queuedLeads.length === 0 ? (
              <Empty
                description="No queued leads found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Table
                rowKey={(record) => record._id || record.leadId}
                columns={columns}
                dataSource={queuedLeads}
                pagination={!isAgent && {
                  current: filters.page,
                  pageSize: filters.limit,
                  total: queuedLeads.length,
                  showSizeChanger: false,
                  showQuickJumper: true
                }}
                size="middle"
                rowClassName={(record) =>
                  record.status === 'assigned' ? 'assigned-row' : 'queued-row'
                }
                onRow={(record) => ({
                  onClick: (e) => {
                    // Only trigger if clicked on the row itself, not on buttons
                    if (!e.target.closest('button') && !e.target.closest('.ant-checkbox-wrapper')) {
                      loadLeadDetails(record._id || record.leadId);
                    }
                  },
                  style: { cursor: 'pointer' }
                })}
              />
            )}
          </Card>
        </TabPane>

        {isAdmin && (
          <TabPane tab={<span><BarChartOutlined /> Analytics</span>} key="analytics">
            {queueStats ? (
              <Alert
                message="Analytics Charts"
                description="Queue statistics and analytics would be displayed here."
                type="info"
                showIcon
              />
            ) : (
              <Empty description="No analytics data available" />
            )}
          </TabPane>
        )}

        {isAdmin && (
          <TabPane tab={<span><LineChartOutlined /> Dashboard</span>} key="dashboard">
            {queueDashboard ? (
              <>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card title="Recent Transfers">
                      {queueDashboard.recentActivity?.transfers?.length > 0 ? (
                        <Timeline>
                          {queueDashboard.recentActivity.transfers.slice(0, 5).map((transfer, index) => (
                            <Timeline.Item key={index} color="blue">
                              {transfer.customerName} • {dayjs(transfer.lastTransfer).fromNow()}
                            </Timeline.Item>
                          ))}
                        </Timeline>
                      ) : (
                        <Empty description="No recent transfers" />
                      )}
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="Oldest Queued">
                      {queueDashboard.recentActivity?.oldestQueued?.map((lead, index) => (
                        <div key={index} style={{ marginBottom: 8 }}>
                          <Text strong>{lead.personalInfo?.fullName}</Text>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {lead.waitTimeMinutes} mins wait • {lead.vehicleInfo?.part}
                          </div>
                        </div>
                      ))}
                    </Card>
                  </Col>
                </Row>
              </>
            ) : (
              <Empty description="No dashboard data available" />
            )}
          </TabPane>
        )}
      </Tabs>

      {/* Modals */}

      {/* Lead Details Modal */}
      <Modal
        title="Lead Details"
        open={leadDetailsModal}
        onCancel={() => {
          setLeadDetailsModal(false);
          setSelectedLead(null);
        }}
        width={800}
        footer={[
          <Button key="close" onClick={() => setLeadDetailsModal(false)}>
            Close
          </Button>,
          isAdmin && selectedLead && (
            <Button
              key="transfer"
              type="primary"
              icon={<UserSwitchOutlined />}
              onClick={() => {
                setLeadDetailsModal(false);
                setTransferModal(true);
              }}
              loading={transferLoading}
              disabled={transferLoading}
            >
              {transferLoading ? 'Loading...' : 'Transfer Lead'}
            </Button>
          )
        ]}
      >
        {selectedLead && (
          <div>
            {selectedLead.assignment?.isExistingCustomer && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Existing customer lead"
                description={
                  selectedLead.assignment?.previousAgent
                    ? `Previous sales agent: ${selectedLead.assignment.previousAgent.name || selectedLead.assignment.previousAgent.email || 'Unknown'}`
                    : 'This lead matches an existing customer record.'
                }
              />
            )}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title="Customer Information" size="small">
                  <p><strong>Name:</strong> {selectedLead.customer.name}</p>
                  <p><strong>Email:</strong> {selectedLead.customer.email}</p>
                  <p>
                    <strong>Phone:</strong>{' '}
                    <span style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                      {formatUSPhone(selectedLead?.customer?.phone || '').formatted}
                    </span>
                  </p>
                  <p><strong>Zip Code:</strong> {selectedLead.customer.zipCode}</p>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Vehicle Information" size="small">
                  <p><strong>Part:</strong> {selectedLead.vehicle.part}</p>
                  <p><strong>VIN:</strong> {selectedLead.vehicle.vin}</p>
                  <p><strong>Year:</strong> {selectedLead.vehicle.year}</p>
                  <p><strong>Make/Model:</strong> {selectedLead.vehicle.make} {selectedLead.vehicle.model}</p>
                </Card>
              </Col>
              <Col span={24}>
                <Card title="Assignment Details" size="small">
                  <Row gutter={16}>
                    <Col span={8}>
                      <p><strong>Tier:</strong> {selectedLead.assignment.tier}</p>
                    </Col>
                    <Col span={8}>
                      <p><strong>Domain:</strong> {selectedLead.assignment.domain}</p>
                    </Col>
                    <Col span={8}>
                      <p><strong>Status:</strong>
                        <Tag color={selectedLead.status === 'assigned' ? 'green' : 'orange'} style={{ marginLeft: 8 }}>
                          {selectedLead.status.toUpperCase()}
                        </Tag>
                      </p>
                    </Col>
                    <Col span={8}>
                      <p><strong>Queue Position:</strong> #{selectedLead.assignment.queuePosition || 'N/A'}</p>
                    </Col>
                    <Col span={8}>
                      <p><strong>Queued At:</strong> {dayjs(selectedLead.assignment.queuedAt).format('MMM DD, YYYY HH:mm')}</p>
                    </Col>
                    <Col span={8}>
                      <p><strong>Estimated Wait:</strong> {selectedLead.assignment.estimatedWaitTime || 0} minutes</p>
                    </Col>
                    <Col span={8}>
                      <p><strong>Score:</strong> {selectedLead.metadata?.score || 0}%</p>
                    </Col>
                    <Col span={8}>
                      <p>
                        <strong>Customer Type:</strong>{' '}
                        <Tag color={selectedLead.assignment?.isExistingCustomer ? 'green' : 'default'}>
                          {selectedLead.assignment?.isExistingCustomer ? 'Existing' : 'New'}
                        </Tag>
                      </p>
                    </Col>
                    {selectedLead.assignment?.previousAgent && (
                      <Col span={16}>
                        <p>
                          <strong>Previous Sales Agent:</strong>{' '}
                          {selectedLead.assignment.previousAgent.name || selectedLead.assignment.previousAgent.email}
                          {selectedLead.assignment.previousAgent.tier ? ` (${selectedLead.assignment.previousAgent.tier})` : ''}
                        </p>
                      </Col>
                    )}
                    {selectedLead.currentAgent && (
                      <Col span={16}>
                        <p><strong>Current Agent:</strong> {selectedLead.currentAgent.name} ({selectedLead.currentAgent.email})</p>
                      </Col>
                    )}
                  </Row>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Transfer Modal */}
      <Modal
        title="Transfer Lead"
        open={transferModal}
        onCancel={() => {
          transferForm.resetFields();
          setTransferModal(false);
          setSelectedLead(null);
        }}
        footer={null}
        width={700}
      >

        {/* ===== LEAD DETAILS CARD - ADD THIS ABOVE YOUR FORM ===== */}
        {selectedLead && (
          <Card
            size="small"
            style={{
              marginBottom: 24,
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #e6e9f0',
              backgroundColor: '#ffffff'
            }}
          >
            {/* Header with Lead Name and Status */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
              borderBottom: '1px solid #f0f2f5',
              paddingBottom: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 4,
                  height: 24,
                  background: selectedLead?.queuePriority === 'High' ? '#ff4d4f' : '#faad14',
                  borderRadius: 4
                }} />
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1a1a1a' }}>
                  {selectedLead?.customer?.name || 'Lead Details'}
                </h3>
              </div>
              <Space size={8}>
                <Tag color={selectedLead?.status === 'queued' ? 'orange' : 'blue'}
                  style={{ borderRadius: 16, padding: '4px 12px' }}>
                  {selectedLead?.status || 'Queued'}
                </Tag>
                {selectedLead?.queuePosition && (
                  <Tag color="geekblue" style={{ borderRadius: 16, padding: '4px 12px' }}>
                    Queue: #{selectedLead.queuePosition}
                  </Tag>
                )}
              </Space>
            </div>

            {/* Main Content Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Left Column - Contact Info */}
              <div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    CONTACT INFORMATION
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedLead?.customer?.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PhoneOutlined style={{ color: '#52c41a', fontSize: 14 }} />
                        <span style={{ color: '#262626' }}>
                          {formatUSPhone(selectedLead.customer.phone).formatted}
                        </span>
                      </div>
                    )}
                    {selectedLead?.customer?.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MailOutlined style={{ color: '#faad14', fontSize: 14 }} />
                        <span style={{ color: '#262626' }}>{selectedLead.customer.email}</span>
                      </div>
                    )}
                    {selectedLead?.customer?.zipCode && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <EnvironmentOutlined style={{ color: '#ff4d4f', fontSize: 14 }} />
                        <span style={{ color: '#262626' }}>ZIP: {selectedLead.customer.zipCode}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Vehicle Info */}
              <div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    VEHICLE DETAILS
                  </div>
                  {selectedLead?.vehicle ? (
                    <div style={{
                      backgroundColor: '#f8f9fc',
                      padding: 12,
                      borderRadius: 8,
                      border: '1px solid #eef2f6'
                    }}>
                      <div style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>
                        {selectedLead.vehicle.year} {selectedLead.vehicle.make} {selectedLead.vehicle.model}
                      </div>
                      {selectedLead.vehicle.part && (
                        <div style={{ fontSize: 13, color: '#595959' }}>
                          Part: {selectedLead.vehicle.part}
                        </div>
                      )}
                      {selectedLead.vehicle.vin && (
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                          VIN: {selectedLead.vehicle.vin}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#8c8c8c' }}>No vehicle details</span>
                  )}
                </div>
              </div>
            </div>

            {/* Domain & Assignment Info */}
            <div style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: '1px solid #f0f2f5',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', gap: 24 }}>
                {selectedLead?.assignment?.domain && (
                  <div>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Domain</span>
                    <div style={{ fontSize: 13, color: '#262626', fontWeight: 500 }}>
                      {selectedLead.assignment.domain}
                    </div>
                  </div>
                )}
                {selectedLead?.assignment?.tier && (
                  <div>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Tier</span>
                    <div style={{ fontSize: 13, color: '#262626' }}>
                      <Tag color={selectedLead.assignment.tier === 'TIER-1' ? 'green' : selectedLead.assignment.tier === 'TIER-2' ? 'blue' : 'purple'}>
                        {selectedLead.assignment.tier}
                      </Tag>
                    </div>
                  </div>
                )}
                {selectedLead?.estimatedWaitTime && (
                  <div>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>Est. Wait</span>
                    <div style={{ fontSize: 13, color: '#262626' }}>
                      {selectedLead.estimatedWaitTime} min
                    </div>
                  </div>
                )}
              </div>

              {selectedLead?.metadata?.createdAt && (
                <Tag icon={<CalendarOutlined />} color="default" style={{ borderRadius: 16, padding: '4px 12px' }}>
                  {new Date(selectedLead.metadata.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </Tag>
              )}
            </div>
          </Card>
        )}

        {selectedLead && (
          <Form form={transferForm} layout="vertical" onFinish={handleTransfer}>

            <Form.Item
              label="Select Agent"
              name="targetAgentId"
              rules={[{ required: true, message: 'Please select an agent' }]}
              help={`Lead Tier: ${selectedLead.assignment.tier}`}
            >
              <Select
                placeholder="Choose agent"
                showSearch
                optionFilterProp="children"
              >
                {allAgents // 👈 USE allAgents INSTEAD OF agents
                  // .filter(agent => agent.subRole === selectedLead.assignment.tier)
                  .map(agent => {
                    const isActive = agent.isActive;
                    const statusColor = isActive ? '#52c41a' : '#ff4d4f';
                    const statusText = isActive ? 'Active' : 'Inactive';

                    return (
                      <Option
                        key={agent._id}
                        value={agent._id}
                        disabled={!isActive} // 👈 Disable if inactive
                      >
                        <Space>
                          <Badge
                            color={statusColor}
                            text={
                              <Space>
                                <Avatar
                                  size="small"
                                  icon={<UserOutlined />}
                                  style={{
                                    backgroundColor: statusColor,
                                    opacity: isActive ? 1 : 0.5
                                  }}
                                />
                                <span style={{ color: isActive ? 'inherit' : '#bfbfbf' }}>
                                  {agent.firstName} {agent.lastName} ({agent.subRole})
                                </span>
                                <Tag size="small" color={isActive ? 'success' : 'error'}>
                                  {statusText}
                                </Tag>
                              </Space>
                            }
                          />
                        </Space>
                      </Option>
                    );
                  })}
              </Select>
            </Form.Item>

            <Form.Item
              label="Reason"
              name="reason"
              initialValue="Manual transfer"
              rules={[{ required: true, message: 'Please provide a reason' }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={transferLoading}
                  disabled={transferLoading}
                >
                  {transferLoading ? 'Transferring...' : 'Transfer'}
                </Button>
                <Button onClick={() => setTransferModal(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>

        )}
      </Modal>

   
      {/* Bulk Transfer Modal */}
      <Modal
        title={`Bulk Transfer (${selectedIds.length} leads)`}
        open={bulkTransferModal}
        onCancel={() => {
          bulkTransferForm.resetFields();
          setBulkTransferModal(false);
        }}
        footer={null}
        width={900}
      >
        {/* ===== LEAD DETAILS CARD FOR BULK TRANSFER ===== */}
        {selectedIds.length > 0 && (
          <Card
            size="small"
            style={{
              marginBottom: 24,
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: '1px solid #e6e9f0',
              backgroundColor: '#ffffff'
            }}
          >
            {/* Header with count */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
              borderBottom: '1px solid #f0f2f5',
              paddingBottom: 12
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 4,
                  height: 24,
                  background: '#1890ff',
                  borderRadius: 4
                }} />
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1a1a1a' }}>
                  Selected Leads ({selectedIds.length})
                </h3>
              </div>
              <Tag color="blue" style={{ borderRadius: 16, padding: '4px 12px' }}>
                Bulk Transfer
              </Tag>
            </div>

            {/* Scrollable lead list */}
            <div style={{
              maxHeight: selectedIds.length > 3 ? 200 : 'auto',
              overflowY: selectedIds.length > 3 ? 'auto' : 'visible',
              paddingRight: 8,
              marginBottom: 8
            }}>
              {queuedLeads
                .filter(lead => selectedIds.includes(lead._id || lead.leadId))
                .map((lead, index) => {
                  const phoneResult = formatUSPhone(lead?.customer?.phone || '');

                  return (
                    <Card
                      key={lead.leadId || lead._id}
                      size="small"
                      style={{
                        marginBottom: 8,
                        backgroundColor: '#fafafa',
                        border: '1px solid #f0f0f0'
                      }}
                    >
                      <Row gutter={16} align="middle">
                        <Col span={2}>
                          <Badge count={index + 1} style={{ backgroundColor: '#1890ff' }} />
                        </Col>

                        <Col span={7}>
                          <div style={{ fontWeight: 500 }}>{lead?.customer?.name || '—'}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            <PhoneOutlined style={{ marginRight: 4 }} />
                            {formatUSPhone(lead?.customer?.phone || '').formatted}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888', marginTop: 2 }}>
                            <MailOutlined style={{ marginRight: 4 }} />
                            {lead?.customer?.email || '—'}
                          </div>
                        </Col>

                        <Col span={7}>
                          <div style={{ fontSize: '12px', fontWeight: 500 }}>
                            <CarOutlined style={{ marginRight: 4 }} />
                            {lead.vehicle.part}
                          </div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {lead.vehicle.year} {lead.vehicle.make} {lead.vehicle.model}
                          </div>
                          <div style={{ fontSize: '11px', color: '#888' }}>
                            <EnvironmentOutlined style={{ marginRight: 4 }} />
                            ZIP: {lead?.customer?.zipCode || 'N/A'}
                          </div>
                          <div style={{ fontSize: '10px', color: '#aaa', marginTop: 2 }}>
                            VIN: {lead.vehicle.vin || 'N/A'}
                          </div>
                        </Col>

                        <Col span={4}>
                          <div>
                            <Tag color={
                              lead.assignment.tier === 'TIER-1' ? 'blue' :
                                lead.assignment.tier === 'TIER-2' ? 'green' : 'orange'
                            }>
                              {lead.assignment.tier}
                            </Tag>
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <Tag color="geekblue" style={{ fontSize: '11px' }}>
                              Score: {lead.metadata?.score || 0}%
                            </Tag>
                          </div>
                        </Col>

                        <Col span={4}>
                          {lead.status === 'queued' && (
                            <>
                              <div style={{ fontSize: '13px', fontWeight: 500 }}>
                                <NumberOutlined /> {lead.queuePosition}
                              </div>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                <FieldTimeOutlined /> Wait: {lead.estimatedWaitTime} min
                              </div>
                              <div style={{ fontSize: '10px', color: '#888', marginTop: 2 }}>
                                {dayjs(lead.assignment.queuedAt).format('MMM DD, YYYY • hh:mm:ss A')}
                              </div>
                            </>
                          )}
                          {lead.status === 'assigned' && (
                            <Tag color="green">Assigned</Tag>
                          )}
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
            </div>

            {/* Summary footer */}
            <div style={{
              marginTop: 8,
              paddingTop: 12,
              borderTop: '1px dashed #e6e9f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '12px',
              color: '#666'
            }}>
              <span>
                <TeamOutlined style={{ marginRight: 4 }} />
                {selectedIds.length} lead{selectedIds.length !== 1 ? 's' : ''} selected
              </span>
              <span>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                Estimated time: {selectedIds.length * 2} mins
              </span>
            </div>
          </Card>
        )}

        <Form layout="vertical" onFinish={handleBulkTransfer} form={bulkTransferForm}>
          <Form.Item
            label="Select Agent"
            name="targetAgentId"
            rules={[{ required: true, message: 'Please select an agent' }]}
            help={`${selectedIds?.length} leads selected`}
          >
            <Select placeholder="Choose agent" showSearch>
              {allAgents?.map(agent => {
                const isActive = agent.isActive;
                const statusColor = isActive ? '#52c41a' : '#ff4d4f';
                const statusText = isActive ? 'Active' : 'Inactive';

                return (
                  <Option
                    key={agent._id}
                    value={agent._id}
                    disabled={!isActive}
                  >
                    <Space>
                      <Badge
                        color={statusColor}
                        text={
                          <Space>
                            <Avatar
                              size="small"
                              icon={<UserOutlined />}
                              style={{
                                backgroundColor: statusColor,
                                opacity: isActive ? 1 : 0.5
                              }}
                            />
                            <span style={{ color: isActive ? 'inherit' : '#bfbfbf' }}>
                              {agent.firstName} {agent.lastName} ({agent.subRole})
                            </span>
                            <Tag size="small" color={isActive ? 'success' : 'error'}>
                              {statusText}
                            </Tag>
                          </Space>
                        }
                      />
                    </Space>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            label="Reason"
            name="reason"
            initialValue="Bulk transfer"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <Select>
              <Option value="Load balancing">Load balancing</Option>
              <Option value="Agent unavailable">Agent unavailable</Option>
              <Option value="Skill-based routing">Skill-based routing</Option>
              <Option value="Manual reassignment">Manual reassignment</Option>
              <Option value="Other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, current) => prev.reason !== current.reason}
          >
            {({ getFieldValue }) =>
              getFieldValue('reason') === 'Other' && (
                <Form.Item
                  name="customReason"
                  rules={[{ required: true, message: 'Please specify reason' }]}
                >
                  <Input placeholder="Enter reason..." />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Transfer {selectedIds.length} Leads
              </Button>
              <Button onClick={() => setBulkTransferModal(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Export Modal */}
      <Modal
        title="Export Queued Leads"
        open={exportModal}
        onCancel={() => setExportModal(false)}
        footer={null}
      >
        <Form layout="vertical" onFinish={handleExport}>
          <Form.Item
            label="Format"
            name="format"
            initialValue="csv"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="csv">CSV File (.csv)</Option>
              <Option value="excel">Excel File (.xlsx)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Tier Filter"
            name="tier"
          >
            <Select allowClear>
              <Option value="TIER-1">Tier 1</Option>
              <Option value="TIER-2">Tier 2</Option>
              <Option value="TIER-3">Tier 3</Option>
            </Select>
          </Form.Item>


          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<DownloadOutlined />}>
                Export
              </Button>
              <Button onClick={() => setExportModal(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QueueDashboard;
